/**
 * Actor v3 APIクライアント
 * プロセス代数とLangChain.js互換性対応
 * https://actor.gftd.ai/api/v3/actor
 */

import {
  ActorID,
  Message,
  State,
  Behavior,
  CreateActorRequest,
  CreateActorResponse,
  SendMessageRequest,
  SendMessageResponse,
  ReceiveMessagesRequest,
  ReceiveMessagesResponse,
  ListActorsResponse,
  ActorSystemConfig,
  SystemStatsResponse,
  ApiV3Response,
  ActorV3Error,
  RunnableConfig,
  LangChainMessage,
  langChainToActorV3,
  actorV3ToLangChain,
  createMessage
} from "@/lib/types/actor-v3"

/**
 * Actor v3 メインクライアント
 */
export class ActorV3Client {
  private baseUrl: string
  private apiKey?: string

  constructor(config: { apiKey?: string; baseUrl?: string } = {}) {
    this.baseUrl = config.baseUrl || "https://actor.gftd.ai/api/v3"
    this.apiKey = config.apiKey
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiV3Response<T>> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    }

    if (this.apiKey) {
      headers.Authorization = `Bearer ${this.apiKey}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.code || 'HTTP_ERROR',
            message: data.message || `HTTP ${response.status}: ${response.statusText}`,
            details: data.details
          }
        }
      }

      return {
        success: true,
        data: data as T
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Unknown network error'
        }
      }
    }
  }

  /**
   * アクター管理API
   */
  actors = {
    /**
     * 新しいアクターを作成
     */
    create: async (request: CreateActorRequest): Promise<ApiV3Response<CreateActorResponse>> => {
      return this.request<CreateActorResponse>("/actor", {
        method: "POST",
        body: JSON.stringify(request),
      })
    },

    /**
     * アクター一覧を取得
     */
    list: async (organizationId?: string): Promise<ApiV3Response<ListActorsResponse>> => {
      const params = organizationId ? `?organization_id=${organizationId}` : ""
      return this.request<ListActorsResponse>(`/actor${params}`)
    },

    /**
     * アクターにメッセージを送信
     */
    send: async (actorId: ActorID, request: SendMessageRequest): Promise<ApiV3Response<SendMessageResponse>> => {
      const messageWithMetadata = {
        ...request.message,
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString()
      }

      return this.request<SendMessageResponse>(`/actor/${actorId}/send`, {
        method: "POST",
        body: JSON.stringify({
          ...request,
          message: messageWithMetadata
        }),
      })
    },

    /**
     * アクターからメッセージを受信
     */
    receive: async (actorId: ActorID, request: ReceiveMessagesRequest = {}): Promise<ApiV3Response<ReceiveMessagesResponse>> => {
      const params = new URLSearchParams()
      if (request.max_messages) params.append("max_messages", request.max_messages.toString())
      if (request.timeout) params.append("timeout", request.timeout.toString())
      if (request.filter?.labels) params.append("filter_labels", request.filter.labels.join(","))
      if (request.filter?.sender) params.append("filter_sender", request.filter.sender)

      const queryString = params.toString()
      const url = `/actor/${actorId}/receive${queryString ? `?${queryString}` : ""}`

      return this.request<ReceiveMessagesResponse>(url)
    },

    /**
     * アクターを削除
     */
    delete: async (actorId: ActorID): Promise<ApiV3Response<{ status: 'deleted' }>> => {
      return this.request(`/actor/${actorId}`, {
        method: "DELETE",
      })
    }
  }

  /**
   * システム管理API
   */
  system = {
    /**
     * システム設定を取得（プロセス代数分析含む）
     */
    getConfig: async (): Promise<ApiV3Response<ActorSystemConfig>> => {
      return this.request<ActorSystemConfig>("/system/config")
    },

    /**
     * システム統計を取得
     */
    getStats: async (): Promise<ApiV3Response<SystemStatsResponse>> => {
      return this.request<SystemStatsResponse>("/system/stats")
    }
  }
}

/**
 * LangChain.js互換性ラッパー
 */
export class ActorV3Runnable {
  constructor(
    private actorId: ActorID,
    private client: ActorV3Client
  ) {}

  /**
   * LangChain.js invoke() 互換メソッド
   */
  async invoke(input: any, config?: RunnableConfig): Promise<any> {
    // 1. LangChain input → Actor v3 Message
    const message = typeof input === 'string' 
      ? createMessage('invoke', { content: input })
      : langChainToActorV3({
          role: 'user',
          content: typeof input === 'string' ? input : JSON.stringify(input)
        })

    // 2. Actor v3 send operation
    const sendResult = await this.client.actors.send(this.actorId, {
      target: this.actorId,
      message
    })

    if (!sendResult.success) {
      throw new Error(`Failed to send message: ${sendResult.error?.message}`)
    }

    // 3. Actor v3 receive operation
    const receiveResult = await this.client.actors.receive(this.actorId, {
      max_messages: 1,
      timeout: config?.timeout || 30000
    })

    if (!receiveResult.success) {
      throw new Error(`Failed to receive message: ${receiveResult.error?.message}`)
    }

    // 4. Actor v3 result → LangChain output
    const { messages, behavior_results } = receiveResult.data!
    
    if (behavior_results.length > 0 && behavior_results[0].actions.length > 0) {
      const outAction = behavior_results[0].actions[0]
      if (outAction.type === 'send' && outAction.message) {
        return actorV3ToLangChain(outAction.message)
      }
    }

    if (messages.length > 0) {
      return actorV3ToLangChain(messages[0])
    }

    return { role: 'assistant', content: 'No response generated' }
  }

  /**
   * LangChain.js stream() 互換メソッド
   */
  async *stream(input: any, config?: RunnableConfig): AsyncGenerator<any, any, unknown> {
    const message = typeof input === 'string'
      ? createMessage('stream', { content: input })
      : langChainToActorV3({
          role: 'user',
          content: typeof input === 'string' ? input : JSON.stringify(input)
        })

    await this.client.actors.send(this.actorId, {
      target: this.actorId,
      message
    })

    // Poll for messages with streaming-like behavior
    let receivedCount = 0
    const maxMessages = config?.maxMessages || 10

    while (receivedCount < maxMessages) {
      const { data } = await this.client.actors.receive(this.actorId, {
        max_messages: 1,
        timeout: 1000
      })

      if (data?.messages && data.messages.length > 0) {
        for (const msg of data.messages) {
          yield actorV3ToLangChain(msg)
          receivedCount++
        }
      } else {
        // No more messages, break
        break
      }
    }
  }

  /**
   * LangChain.js batch() 互換メソッド
   */
  async batch(inputs: any[], config?: RunnableConfig): Promise<any[]> {
    const promises = inputs.map(input => this.invoke(input, config))
    return Promise.all(promises)
  }

  /**
   * LangChain.js pipe() 互換メソッド
   */
  pipe<T>(next: T): T {
    // 簡単な実装。実際にはパイプライン機能を実装する必要がある
    return next
  }

  /**
   * LangChain.js withFallbacks() 互換メソッド
   */
  withFallbacks(fallbacks: any[]): ActorV3Runnable {
    // 簡単な実装。実際にはフォールバック機能を実装する必要がある
    return this
  }
}

/**
 * デフォルトクライアントインスタンス
 */
export const actorV3Api = new ActorV3Client()

/**
 * サンプルBehavior作成ヘルパー
 */
export function createSampleBehavior(name: string, description: string): Behavior {
  return {
    id: crypto.randomUUID(),
    name,
    description,
    version: '1.0.0',
    handler: async (message: Message, state: State) => {
      // サンプル実装：エコー機能
      return {
        new_state: { ...state, last_message: message },
        actions: [{
          type: 'send',
          target: message.sender,
          message: createMessage('response', {
            content: `Echo: ${JSON.stringify(message.payload)}`
          })
        }]
      }
    },
    metadata: {
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      author: 'system',
      tags: ['sample', 'echo'],
      capabilities: ['message_processing']
    },
    process_algebra: {
      notation: 'CCS',
      expression: `${name}(x) = x?.input(msg).x!output(echo(msg)).${name}(x)`,
      channels: ['input', 'output']
    }
  }
} 